import {
  ProjectSchema,
  ZodCreateProjectSchema,
  ZodGetProjectsParams,
  ZodUpdateProjectSchema,
} from "@billingnow/zod-schema";
import { PaginationMeta, PaginationParams } from "./pagination";
import { apiClient } from "./client";

export type Project = ProjectSchema;

/**
 * Get a single project by ID
 */
export const getProject = async (projectId: string): Promise<Project> => {
  const response = await apiClient.get<{ project: Project }>(`/api/project/${projectId}`);
  return response.data.project;
};

/**
 * Get all projects - returns all projects (for backward compatibility)
 * @deprecated Use getProjectsPaginated for better performance with large datasets
 */
export const getProjects = async (params?: ZodGetProjectsParams): Promise<Project[]> => {
  const response = await apiClient.get<{ projects: Project[]; pagination: PaginationMeta }>("/api/project", { params });
  return response.data.projects;
};

/**
 * Paginated response for projects
 */
export interface GetProjectsPaginatedResponse {
  projects: Project[];
  pagination: PaginationMeta;
}

/**
 * Get projects with pagination support
 */
export const getProjectsPaginated = async (
  params?: ZodGetProjectsParams & PaginationParams,
): Promise<GetProjectsPaginatedResponse> => {
  const response = await apiClient.get<GetProjectsPaginatedResponse>("/api/project", { params });
  return response.data;
};

/**
 * Create a new project
 */
export const createProject = async (projectData: ZodCreateProjectSchema): Promise<Project> => {
  const response = await apiClient.post<{ project: Project }>("/api/project", projectData, {
    headers: { "Content-Type": "application/json" },
  });
  return response.data.project;
};

/**
 * Update an existing project
 */
export const updateProject = async (projectData: ZodUpdateProjectSchema): Promise<Project> => {
  const response = await apiClient.put<{ project: Project }>(`/api/project/${projectData.id}`, projectData, {
    headers: { "Content-Type": "application/json" },
  });
  return response.data.project;
};

/**
 * Delete a project
 */
export const deleteProject = async (projectId: string): Promise<{ message: string }> => {
  const response = await apiClient.delete<{ message: string }>(`/api/project/${projectId}`);
  return response.data;
};

export default getProject;
